import React, { useCallback, useMemo } from 'react'
import styled from 'styled-components'
import { AppError, fromUnknownError } from '../../error'
import Loader from './loader'
import { ButtonLink } from './button'
import { signOut } from 'firebase/auth'
import { auth } from '~/utils/client/auth'

const Wrapper = styled.div<{ $hasError: boolean }>`
  display: flex;
  flex: 1;
  flex-direction: ${({ $hasError }) => ($hasError ? 'column' : 'row')};
  text-align: center;
  justify-content: center;
  align-items: center;

  .svg {
    display: flex;
  }
  .error-message {
    display: flex;
    flex-direction: column;
    margin-bottom: 20px;
    margin-top: 20px;
    align-items: center;

    small {
      font-size: 20px;
      color: ${({ theme }) => theme.colors.primary};
    }
    span {
      font-weight: bold;
      color: ${({ theme }) => theme.colors.darkGrey};
      font-size: 28px;
      color: ${({ theme }) => theme.colors.primary};
    }
  }
`

const Button = styled.button`
  display: center;
  text-decoration: none;
  font-size: 1rem;
  font-weight: 700;
  background-color: ${({ theme }) => theme.colors.primary};
  color: #fff;
  border-radius: 4px;
  align-self: center;
  padding: 0.5rem 2rem;
  margin: 0.625rem;
  transition-duration: 0.4s;
  border: 1px solid transparent;
  cursor: pointer;
  span {
    font-size: 16px;
    color: #fff;
  }
  &:hover {
    color: #fff;
    background-color: ${({ theme }) => theme.colors.secondary};
    border: 1px solid #fff;
  }
`

export interface HandleProps extends React.HTMLAttributes<HTMLDivElement> {
  isLoading?: boolean
  alwaysRenderChildren?: boolean
  error?: Error | AppError | null | undefined
  onTryAgain?: () => void
  tryAgainButtonLabel?: string
  loginAction?: boolean
  children?: React.ReactNode
}

export default function Handle({
  alwaysRenderChildren = false,
  isLoading = false,
  error = null,
  children = null,
  onTryAgain,
  tryAgainButtonLabel = 'Tentar novamente',
  loginAction,
  ...props
}: HandleProps) {
  const finalError = useMemo(() => {
    if (!error) {
      return null
    }
    return fromUnknownError(error)
  }, [error])

  const handleTryAgainClick = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault()
      if (!onTryAgain) {
        return
      }
      onTryAgain()
    },
    [onTryAgain],
  )

  const handleLogout = useCallback(async () => {
    try {
      await signOut(auth)
    } catch (error) {
      console.error(error)
      window.location.reload()
    }
  }, [])

  if (isLoading) {
    return (
      <Wrapper $hasError={!!finalError} {...props}>
        <Loader /> <br />
        {alwaysRenderChildren ? children : null}
      </Wrapper>
    )
  }
  if (!finalError) {
    return children as React.ReactElement
  }
  return (
    <Wrapper $hasError={!!finalError} {...props}>
      <div className='error-message'>
        {finalError.key !== 'auth' && finalError.key !== 'permission' ? (
          <>
            <small>Ocorreu um erro</small>
            <br />
          </>
        ) : null}
        <span>{finalError.message}</span>
      </div>
      {onTryAgain ? <Button onClick={handleTryAgainClick}>{tryAgainButtonLabel}</Button> : null}
      {finalError.key === 'auth' || finalError.key === 'permission' ? (
        <ButtonLink onGo={handleLogout}>Sair</ButtonLink>
      ) : null}
      {isLoading ? <Loader /> : null}
      {alwaysRenderChildren ? children : null}
    </Wrapper>
  )
}
